import React, { useState, useEffect, useCallback } from "react";
import { Theme, WithStyles, withStyles, withTheme, WithTheme } from "@material-ui/core/styles";
import RefreshIconButton from "../../../../common/RefreshIconButton";
import { formatElapsedSecondsForDisplay } from "../../../../modules/Dispatch/utils/utils";

const REFRESH_TIME_UPDATE_INTERVAL_MS = 5000;

type ThemeWithCustom = Theme & {
  dispatchCardTextFontSize?: string;
  lastRefreshTextColor?: string;
  gridTextColor?: string;
  iconColor?: string;
};

const styles = (theme: Theme) => {
  const t = theme as ThemeWithCustom;
  return {
    actions: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    lastRefreshedText: {
      fontSize: t.dispatchCardTextFontSize ?? "12px",
      color: t.lastRefreshTextColor ?? t.gridTextColor,
    },
  };
};

export type RefreshStatusProps = WithStyles<typeof styles> & {
  onRefresh?: () => void;
  defaultTooltip?: string;
  refreshingTooltip?: string;
};

type RefreshStatusInnerProps = RefreshStatusProps & WithTheme;

const RefreshStatusInner = (props: RefreshStatusInnerProps): React.ReactElement | null => {
  const { classes, theme, onRefresh, defaultTooltip, refreshingTooltip } = props;
  const t = theme as ThemeWithCustom;
  const [lastRefreshTimestamp, setLastRefreshTimestamp] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  const updateElapsed = useCallback(() => {
    if (lastRefreshTimestamp == null) return;
    setElapsedSeconds(Math.floor((Date.now() - lastRefreshTimestamp) / 1000));
  }, [lastRefreshTimestamp]);

  useEffect(() => {
    if (lastRefreshTimestamp == null) return;
    updateElapsed(); // run immediately so we show "0 sec ago"
    const id = setInterval(updateElapsed, REFRESH_TIME_UPDATE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [lastRefreshTimestamp, updateElapsed]);

  const handleRefreshClick = useCallback(() => {
    setLastRefreshTimestamp(Date.now());
    setElapsedSeconds(0);
    onRefresh?.();
  }, [onRefresh]);

  if (!onRefresh) return null;

  return (
    <div className={classes.actions}>
      {lastRefreshTimestamp != null && (
        <span className={classes.lastRefreshedText}>
          Last refreshed {formatElapsedSecondsForDisplay(elapsedSeconds)}
        </span>
      )}
      <RefreshIconButton
        onRefresh={handleRefreshClick}
        defaultTooltip={defaultTooltip ?? "Refresh"}
        refreshingTooltip={refreshingTooltip ?? "Refreshing…"}
        iconColor={t.iconColor}
      />
    </div>
  );
};

export default withStyles(styles)(withTheme()(RefreshStatusInner));
